import { CheckboxItems } from '@own/fds-react/dist/types/checkbox.types';
import { VehicleType } from '../../../models/experiencefragments/vehicle-type';
import {
    FeatureKey,
    SinglePublicFeature,
    SupportedMarkets,
    UiFeature,
} from './vehicle-features.types';
import slugify from 'slugify';

export const CATEGORY_LANGUAGE_MAP: Record<
    SupportedMarkets,
    Record<FeatureKey, SinglePublicFeature>
> = {
    'en-us': {
        ELECTRIC_VEHICLES: {
            displayName: 'Electric Vehicles',
            queryName: 'electric-vehicles',
        },
        SYNC: {
            displayName: 'SYNC',
            queryName: 'sync',
        },
        FORD_TECHNOLOGY: {
            displayName: 'Ford Technology',
            queryName: 'ford-technology',
        },
    },
    'en-ca': {
        ELECTRIC_VEHICLES: {
            displayName: 'Electric Vehicles',
            queryName: 'electric-vehicles',
        },
        SYNC: {
            displayName: 'SYNC',
            queryName: 'sync',
        },
        FORD_TECHNOLOGY: {
            displayName: 'Ford Technology',
            queryName: 'ford-technology',
        },
    },
    'fr-ca': {
        ELECTRIC_VEHICLES: {
            displayName: 'Véhicules Électriques',
            queryName: 'vehicules-electriques',
        },
        SYNC: {
            displayName: 'SYNC',
            queryName: 'sync',
        },
        FORD_TECHNOLOGY: {
            queryName: 'technologie-ford',
            displayName: 'Technologie Ford',
        },
    },
};

export const getPublicCategories = (
    vehicleType: VehicleType,
    currentLanguageRegionCode: SupportedMarkets
): SinglePublicFeature[] => {
    const unauthFeatureList =
        UNAUTH_CATEGORY_MAP[vehicleType][currentLanguageRegionCode];
    return [unauthFeatureList.feature1, unauthFeatureList.feature2];
};

export const getHiddenCategories = (
    vehicleType: VehicleType,
    currentLanguageRegionCode: SupportedMarkets,
    allCategoryNames: string[]
): SinglePublicFeature[] => {
    const publicCategoryNames = getPublicCategories(
        vehicleType,
        currentLanguageRegionCode
    ).flatMap(category => category.displayName);

    return allCategoryNames
        .filter(item => publicCategoryNames.indexOf(item) === -1)
        .map(c => ({
            displayName: c,
            queryName: slugify(c, { lower: true }),
        }));
};

export const UNAUTH_CATEGORY_MAP: Record<
    VehicleType,
    Record<
        SupportedMarkets,
        { feature1: SinglePublicFeature; feature2: SinglePublicFeature }
    >
> = {
    bev: getCategoryMap('ELECTRIC_VEHICLES', 'FORD_TECHNOLOGY'),
    bevTruck: getCategoryMap('ELECTRIC_VEHICLES', 'FORD_TECHNOLOGY'),
    ice: getCategoryMap('FORD_TECHNOLOGY', 'SYNC'),
    truck: getCategoryMap('FORD_TECHNOLOGY', 'SYNC'),
};

function getCategoryMap(
    feature1: FeatureKey,
    feature2: FeatureKey
): Record<
    SupportedMarkets,
    { feature1: SinglePublicFeature; feature2: SinglePublicFeature }
> {
    const returnObject = ['en-us', 'en-ca', 'fr-ca'].reduce((item, lang) => {
        item = {
            ...item,
            [lang]: {
                feature1:
                    CATEGORY_LANGUAGE_MAP[lang as SupportedMarkets][feature1],
                feature2:
                    CATEGORY_LANGUAGE_MAP[lang as SupportedMarkets][feature2],
            },
        };
        return item;
    }, {});

    return returnObject as Record<
        SupportedMarkets,
        { feature1: SinglePublicFeature; feature2: SinglePublicFeature }
    >;
}

export const extractCategories = (features: UiFeature[]): CheckboxItems[] => {
    const categories: CheckboxItems[] = [];
    features.forEach(feature => {
        if (feature.categoryNames) {
            feature.categoryNames.forEach(category => {
                if (
                    !categories.some(
                        item =>
                            item.name === category && feature.longDescAvailable
                    )
                ) {
                    if (
                        features
                            .filter(item => item.longDescAvailable)
                            .some(filteredCategory =>
                                filteredCategory.categoryNames?.includes(
                                    category
                                )
                            )
                    ) {
                        categories.push({
                            name: category,
                            value: category,
                            ariaLabel: category,
                            itemName: category,
                            onClick: () => null,
                            checked: false,
                        });
                    }
                }
            });
        }
    });

    return categories.sort((a, b) => a.name.localeCompare(b.name));
};
